import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, MenuItem, Select, Typography, IconButton, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { AppointmentOrderData, UpdateOrderData } from '../types/appointmentOrders.types';
import OrderItemsGrid from './OrderItemsGrid';
import BillingDialogPaymentSection, { PaymentSplit } from './BillingDialogPaymentSection';
import { v4 as uuidv4 } from 'uuid';

import { OrderItem } from '../api/orderItemsApi';

export interface BillingDialogProps {
  open: boolean;
  onClose: () => void;
  orders: AppointmentOrderData[];
  itemsMaster: OrderItem[];
  loading?: boolean;
}

const paymentModes = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'NETBANKING', label: 'Net Banking' },
  { value: 'WaveOff', label: 'WaveOff' },
];

import { appointmentOrdersApi } from '../api/appointmentOrdersApi';
import { createPaymentReceiveComplete } from '../../payments/api/paymentReceiveCompleteApi';
import { toast } from 'react-toastify';

const BillingDialog: React.FC<BillingDialogProps> = ({ open, onClose, orders, itemsMaster, loading }) => {
  const [comment, setComment] = useState<string>("");
const [lastComment, setLastComment] = useState<string>("");

  // Restore last comment when dialog is opened and comment is empty
  React.useEffect(() => {
    if (!open) return;
    if (comment === "" && lastComment !== "") {
      setComment(lastComment);
    }
    // Only run when dialog is opened or comment changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [isPostingPayment, setIsPostingPayment] = useState(false);
  // Only include orders with relevant statuses for billing
  const includedStatuses = ['ORDERED', 'BILLED', 'PARTIALLY_PAID'];
  const filteredOrders = orders.filter(order => includedStatuses.includes((order.financialStatus || '').toUpperCase()));
console.log('All order statuses:', orders.map(o => o.financialStatus));
  // Cap paid amounts to line total after price/discount changes
  const capPaidAmountsToLineTotal = () => {
    setBillingItems(prevItems =>
      prevItems.map(item => {
        const lineTotal = ((item.price || 0) * (item.quantity || 1)) - (item.discountValue || 0);
        if (item.paidAmount !== undefined && item.paidAmount > lineTotal) {
          return { ...item, paidAmount: lineTotal };
        }
        return item;
      })
    );
  };

  // Distribute a total paid amount across items
  const distributePaidAmount = (totalPaid: number) => {
  setBillingItems(prevItems => {
    let remaining = totalPaid;
    let overpaymentDetected = false;
    const updatedItems = prevItems.map(item => {
      // Always use backend pendingAmount for cap
      const pending = typeof item.pendingAmount === 'number' ? item.pendingAmount : 0;
      const toPay = Math.max(0, Math.min(pending, remaining));
      if (toPay > pending) {
        overpaymentDetected = true;
      }
      remaining -= toPay;
      return {
        ...item,
        newPaidAmount: toPay,
        paidAmount: toPay // for this session, always just newPaidAmount
      };
    });
    if (overpaymentDetected) {
      toast.error('Cannot pay more than pending amount for any item.');
    }
    return updatedItems;
  });
};

  

  const [paymentSplits, setPaymentSplits] = useState<PaymentSplit[]>([
    { id: uuidv4(), mode: 'CASH', amount: 0, reference: '', date: new Date().toISOString() }
  ]);

  // Format currency helper
  const formatCurrency = (value: number) => `₹${value.toFixed(2)}`;
  // Calculate total discount
  const calculateTotalDiscount = () => billingItems.reduce((sum, item) => sum + (item.discountValue || 0), 0);
  // Calculate total due (before previously paid)
  const calculateTotalDue = () => billingItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1) - (item.discountValue || 0)), 0);
  // Calculate amount paid (this session)
  const calculateAmountPaid = () => paymentSplits.reduce((sum, p) => sum + (p.amount || 0), 0);
  // Calculate outstanding due (after previously paid, before this session)
  const calculateOutstandingDue = () => calculateTotalDue() - billingItems.reduce((sum, item) => sum + (item.itemPaidStatic || 0), 0);
  // Calculate remaining due (negative = change due)
  const calculateRemainingDue = () => calculateOutstandingDue() - calculateAmountPaid();
  const [billingItems, setBillingItems] = useState<AppointmentOrderData[]>(filteredOrders);
  // Track which items have been manually edited by the user
  const [userEditedPaidAmounts, setUserEditedPaidAmounts] = useState<Set<string>>(new Set());
  const [excludedFromBilling, setExcludedFromBilling] = useState<string[]>([]);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);

  // Handler to exclude/include an item from billing
  const handleExcludeFromBilling = (itemId: string) => {
    setExcludedFromBilling(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Reset billingItems, userEditedPaidAmounts, and paymentSplits whenever dialog opens or orders change
  React.useEffect(() => {
    setBillingItems(filteredOrders.map(item => {
  const paid = typeof item.paidAmount === 'number' ? item.paidAmount : 0;
      const pending = typeof item.pendingAmount === 'number'
        ? item.pendingAmount
        : ((item.price || 0) * (item.quantity || 1) - paid);
      return {
        ...item,
        itemPaidStatic: paid,
        newPaidAmount: 0, // always zero for new session
        paidAmount: 0,    // always zero for new session
        pendingAmount: pending
      };
}));
    setPaymentSplits([
      { id: uuidv4(), mode: 'CASH', amount: 0, reference: '', date: new Date().toISOString() }
    ]);
  }, [orders, open]);

  // State for editing price and discount
  
  // State for editing paid amount
  const [editingPaidAmount, setEditingPaidAmount] = useState<{ id: string; value: string } | null>(null);
  

  // Automatically distribute paid amount when paymentSplits changes
  React.useEffect(() => {
    // Sum all paymentSplits amounts
    const totalPaid = paymentSplits.reduce((sum, p) => sum + (p.amount || 0), 0);
    if (totalPaid > 0) {
      distributePaidAmount(totalPaid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSplits]);

  // Handler for updating paid amount
  const handleUpdatePaidAmount = (itemId: string) => {
  if (!editingPaidAmount) return;
  // Treat empty input as zero
  let newValue = editingPaidAmount.value === "" ? 0 : parseFloat(editingPaidAmount.value);
  if (isNaN(newValue) || newValue < 0) return;
  const item = billingItems.find(i => i.id === itemId);
  if (!item) return;
  const pending = typeof item.pendingAmount === 'number'
    ? item.pendingAmount
    : (((item.price || 0) * (item.quantity || 1)) - (item.discountValue || 0) - (item.itemPaidStatic ?? 0));
  
  setBillingItems(prevItems =>
  prevItems.map(i =>
    i.id === itemId
      ? {
          ...i,
          newPaidAmount: newValue,
          paidAmount: (i.itemPaidStatic ?? 0) + newValue
        }
      : i
  )
);
setUserEditedPaidAmounts(prev => new Set(prev).add(itemId));
  setEditingPaidAmount(null);
};


  // Handler for deleting an item
  const handleDeleteItem = (itemId: string) => {
    setBillingItems(prev => prev.filter(item => item.id !== itemId));
    setDeletedItems(prev => [...prev, itemId]);
  };

  // Handle billing (update/cancel orders)
  // Handle billing (update/cancel orders)
// payLater: if true, do not update paidAmount, only update price, qty, discount, grandTotal, and set financialStatus to 'BILLED'
const handleBill = async (isPayLater: boolean) => {
    // Exclude items marked for exclusion
    const filteredBillingItems = billingItems.filter(item => !excludedFromBilling.includes(item.id));
    try {
      // Mark deleted items as CANCELLED, others as BILLED (or PAID)
      const allOrderIds = orders.map(o => o.id);
      const updatedOrders = allOrderIds.map(orderId => {
    // Skip excluded items
    if (excludedFromBilling.includes(orderId)) return undefined;
    const edited = billingItems.find(b => b.id === orderId);
    if (edited) {
      const price = edited.price;
      const quantity = edited.quantity || 1;
      const discountValue = edited.discountValue || 0;
      const grandTotal = (price * quantity) - discountValue;
      // If Pay Later, do not update paidAmount, keep backend's original
      if (isPayLater) {
        return {
          id: edited.id,
          price,
          quantity,
          discountValue,
          grandTotal,
          financialStatus: 'BILLED'
        };
      } else {
        const prevPaid = edited.itemPaidStatic ?? 0;
        const newPaid = edited.newPaidAmount || 0;
        const paidAmount = newPaid; // Only the new payment for this session
        let status: 'PAID' | 'PARTIALLY_PAID' | 'BILLED' = 'BILLED';
        if (paidAmount >= grandTotal) {
          status = 'PAID';
        } else if (paidAmount > 0 && paidAmount < grandTotal) {
          status = 'PARTIALLY_PAID';
        }
        return {
          id: edited.id,
          price,
          quantity,
          discountValue,
          grandTotal,
          paidAmount, // Cumulative: previous + new
          financialStatus: status
        };
      }
    } else {
      // Always include deleted/cancelled items
      const original = orders.find(o => o.id === orderId);
      if (original) {
        return {
          id: original.id,
          financialStatus: 'CANCELLED',
          price: original.price,
          discountValue: original.discountValue,
          quantity: original.quantity,
          grandTotal: original.grandTotal,
          paidAmount: 0,
          procedureStatus: original.procedureStatus,
        };
      }
      return undefined;
    }
  }).filter(Boolean) as UpdateOrderData[];

      console.log('Sending updatedOrders to backend:', updatedOrders);
      // Update each order one by one using the update-by-id endpoint
      await Promise.all(
        updatedOrders.map(({ id, ...orderWithoutId }) => {
          console.log('[BillingDialog] PATCH order', id, orderWithoutId);
          return appointmentOrdersApi.updateOrderById(id, orderWithoutId);
        })
      );

      // Optionally: show success, close dialog, refresh parent, etc.
      if (onClose) onClose();
    } catch (error) {
      // Optionally: show error
      console.error('Billing failed', error);
    }
  };


  // Handle quantity change for order items (like AppointmentDetails)
  const handleQuantityChange = (itemId: string, delta: number) => {
    setBillingItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const newQuantity = (item.quantity || 1) + delta;
          return {
            ...item,
            quantity: Math.max(1, newQuantity)
          };
        }
        return item;
      })
    );
  };

  // Handle grid cell change (for other editable fields)
  const handleGridChange = (id: string, field: keyof AppointmentOrderData, value: any) => {
    setBillingItems(prev => prev.map(item => item.id === id ? { ...item, [field]: field === 'discountValue' ? Number(value) : value } : item));
  };

  // Totals (tax removed)
  const totals = useMemo(() => {
    let total = 0, discount = 0, grand = 0;
    billingItems.forEach(item => {
      const itemTotal = (item.price || 0) * (item.quantity || 1);
      const itemDiscount = item.discountValue || 0;
      const lineTotal = itemTotal - itemDiscount;
      total += itemTotal;
      discount += itemDiscount;
      grand += lineTotal;
    });
    return { total, discount, grand };
  }, [billingItems]);

  // Columns for grid (not used in custom table, but kept for reference)
  // Tax column removed
  const columns: { key: keyof AppointmentOrderData; label: string; editable?: boolean; type?: 'number' | 'text' | 'select'; adornment?: string }[] = [
    { key: 'orderNumber', label: 'Order #' },
    { key: 'itemId', label: 'Item ID' },
    { key: 'originalPrice', label: 'Original Price', type: 'number', adornment: '₹' },
    { key: 'price', label: 'Selling Price', type: 'number', adornment: '₹' },
    { key: 'discountValue', label: 'Discount', editable: true, type: 'number', adornment: '₹' },
    { key: 'quantity', label: 'Qty', editable: true, type: 'number' },
    { key: 'total', label: 'Line Total', type: 'number', adornment: '₹' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Billing ({billingItems.length} item{billingItems.length !== 1 ? 's' : ''})</DialogTitle>
      <DialogContent>
        {/* Custom Order Items Table with advanced price/discount editing */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Order Items</Typography>
          {billingItems.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
              No orders to bill.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>Item Name</TableCell>
                    <TableCell sx={{ width: '13%' }} align="right">Original Price</TableCell>
                    <TableCell sx={{ width: '15%' }} align="right">Selling Price</TableCell>
                    <TableCell sx={{ width: '15%' }} align="center">Discount</TableCell>
                    <TableCell sx={{ width: '10%' }} align="center">Qty</TableCell>
                    <TableCell sx={{ width: '15%' }} align="right">Line Total</TableCell>
                    <TableCell sx={{ width: '10%' }} align="right">Paid Amount</TableCell>
                    <TableCell sx={{ width: '10%' }} align="right">Pending Amount</TableCell>
                    <TableCell sx={{ width: '6%' }} align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  
                  {billingItems.map((item, idx) => {
                    const sellingPrice = item.price || 0;
                    const quantity = item.quantity || 1;
                    const discount = item.discountValue || 0;
                    const lineTotal = (sellingPrice * quantity) - discount;
                    const paidAmount = typeof item.paidAmount === 'number' ? item.paidAmount : 0;
                    const pendingAmount = typeof item.pendingAmount === 'number' ? item.pendingAmount : 0;
                    return (
                      <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        {/* Show item name from backend response, fallback to itemId */}
                        <TableCell>{item.item?.itemName || item.itemId}</TableCell>
                        <TableCell align="right">₹{item.originalPrice?.toFixed(2)}</TableCell>
                        {/* Selling Price cell */}
                        <TableCell align="right">
                          ₹{item.price?.toFixed(2)}
                          {item.originalPrice && item.originalPrice > item.price && (
                            <Typography variant="caption" color="success.main" display="block">
                              Save ₹{(item.originalPrice - item.price).toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        {/* Discount cell */}
                        <TableCell align="center">
                          {item.discountValue > 0 ? (
                            <Box>
                              <Box color="error.main">
                                {`₹${item.discountValue}`}
                              </Box>
                              <Box fontSize="0.7rem" color="text.secondary">
                                {'Fixed discount'}
                              </Box>
                            </Box>
                          ) : (
                            <Typography color="text.secondary" variant="body2">—</Typography>
                          )}
                        </TableCell>
                        {/* Qty */}
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, -1)}
                              disabled={item.quantity <= 1}
                              aria-label="Decrease quantity"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Box px={1} minWidth={24} textAlign="center">
                              {item.quantity}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, 1)}
                              aria-label="Increase quantity"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {/* Line Total */}
                        <TableCell align="right">₹{lineTotal.toFixed(2)}</TableCell>
                        {/* Paid Amount */}
                        <TableCell align="right">₹{paidAmount.toFixed(2)}</TableCell>
                        {/* Pending Amount */}
                        <TableCell align="right">₹{pendingAmount.toFixed(2)}</TableCell>
                        {/* Delete Icon */}
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
        {/* Payment Section - matches Order Dialog */}
        <BillingDialogPaymentSection
          paymentSplits={paymentSplits}
          setPaymentSplits={setPaymentSplits}
          paymentModes={paymentModes}
          orderItems={billingItems}
          formatCurrency={formatCurrency}
          calculateTotalDiscount={calculateTotalDiscount}
          calculateTotalDue={calculateTotalDue}
          calculateAmountPaid={calculateAmountPaid}
          calculateRemainingDue={calculateRemainingDue}
          comment={comment}
          setComment={setComment}
          distributePaidAmount={distributePaidAmount}
          totalPreviouslyPaid={billingItems.reduce((acc, item) => acc + (item.itemPaidStatic || 0), 0)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button 
          variant="contained"
          color="secondary"
          onClick={() => {
  setLastComment(comment);
  setComment("");
  handleBill(true);
}}
          disabled={billingItems.length === 0 || loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Processing...' : 'Pay Later'}
        </Button>
        <Button 
          variant="contained"
          color="primary"
          onClick={async () => {
            setLastComment(comment);
            setComment("");
            setIsPostingPayment(true);
            try {
              // Validate reference numbers for non-cash payments
              const missingReference = paymentSplits.some(
                split => split.mode !== 'CASH' && !split.reference
              );
              if (missingReference) {
                toast.error('Reference number is required for non-cash payments.');
                setIsPostingPayment(false);
                return;
              }
              // Transactional payment receive (parent + children)
            const nowIsoString = new Date().toISOString();
            const totalPaid = calculateAmountPaid();
const totalDue = calculateTotalDue();
const changesDue = totalPaid > totalDue ? totalPaid - totalDue : 0;
const totalDiscount = calculateTotalDiscount();
const paymentReceivePayload = {
  paymentTransactionNumber: '', // let backend generate, required by TS type
  paymentDate: nowIsoString,
  totalPaymentReceived: totalPaid, // total paid by user
  status: 'RECEIVED',
  patientId: billingItems[0]?.patientId,
  createdBy: '', // fill from context/user
  createdAt: nowIsoString,
  updatedAt: nowIsoString,
  isDeleted: false,
  comment,
  totalReceived: totalDue, // total amount due (before payment)
  changesDue,
  totalDiscount,
};
            // Only include orders with paidAmount > 0
            const ordersWithPayment = billingItems.filter(item => (item.paidAmount || 0) > 0);
            const paymentReceiveOrders = ordersWithPayment.map(item => ({
  appointmentOrderId: item.id,
  amountApplied: item.paidAmount || 0,
  createdBy: '', // fill from context/user
  createdAt: nowIsoString,
  isDeleted: false,
  price: item.price,
  quantity: item.quantity || 1,
  discountValue: item.discountValue || 0,
  grandTotal: ((item.price || 0) * (item.quantity || 1)) - (item.discountValue || 0),
  financialStatus: (() => {
    const paid = item.paidAmount || 0;
    const total = ((item.price || 0) * (item.quantity || 1)) - (item.discountValue || 0);
    if (paid >= total) return 'PAID';
    if (paid > 0 && paid < total) return 'PARTIALLY_PAID';
    return 'BILLED';
  })()
}));
            const paymentReceivePaymentModes = paymentSplits.map(split => ({
              paymentMode: split.mode,
              amount: split.amount,
              reference: split.reference,
              createdBy: '', // fill from context/user,
              createdAt: nowIsoString,
              isDeleted: false,
            }));
            const completePayload = {
              paymentReceive: paymentReceivePayload,
              paymentReceiveOrders,
              paymentReceivePaymentModes,
            };
            console.log('[ReceivePayment] === TRANSACTIONAL PAYMENT POST PAYLOAD ===');
            console.log(JSON.stringify(completePayload, null, 2));
            console.log('[ReceivePayment] ==========================================');
            const resp = await createPaymentReceiveComplete(completePayload);
            if (resp.success) {
              toast.success('Payment received successfully!');
              if (onClose) onClose();
            } else {
              toast.error('Payment failed: ' + (resp.message || 'Unknown error'));
            }
            } catch (err) {
              toast.error('Failed to receive payment.');
              console.error('[ReceivePayment] Payment receive error:', err);
            } finally {
              setIsPostingPayment(false);
            }
          }}
          disabled={billingItems.length === 0 || loading || isPostingPayment || calculateAmountPaid() <= 0}
          sx={{ 
            minWidth: 140,
            '&:disabled': {
              opacity: 0.7,
              cursor: 'not-allowed'
            }
          }}
        >
          {isPostingPayment ? 'Processing...' : 'Receive Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingDialog;
